import { useEffect, useMemo, useState } from "react";
import { Sparkles, Info, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const eur = (v) => Number(v).toLocaleString("it-IT", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });

function pickTier(tiers, kwp) {
  if (!tiers) return null;
  const k = Number(kwp || 0);
  return tiers.find((t) => k <= t.max_kw) || null;
}

// Badge versione nel portale cliente. Mostra:
// - "Versione Pro" se attiva e pagata
// - "Versione Pro · Concessione gratuita" se attiva ma senza abbonamento
// - "Passa a Versione Pro" (con popup di abbonamento) se non attiva
export function VersioneProBadge({ pro, proPaid, trial, kwp, src }) {
  const [open, setOpen] = useState(false);
  const [tar, setTar] = useState(null);
  const [interval, setInterval] = useState("year");
  const [busy, setBusy] = useState(false);
  const canBuy = !!src?.proCheckout;

  useEffect(() => {
    if (open && canBuy && !tar) src.proTariffe().then(setTar).catch(() => {});
  }, [open, canBuy, tar, src]);

  const tier = useMemo(() => pickTier(tar?.tiers, kwp), [tar, kwp]);

  if (pro) {
    const g = trial?.giorni_rimanenti;
    const countdown = trial?.attivo
      ? g === 0 ? "scade oggi" : g === 1 ? "scade domani" : `scade tra ${g} giorni`
      : null;
    const fineIt = trial?.fine ? new Date(trial.fine).toLocaleDateString("it-IT") : null;
    return (
      <span data-testid="cliente-pro-badge"
        title={countdown ? `Concessione gratuita attivata con l'impianto · scade il ${fineIt}` : undefined}
        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-[#C17F59] to-[#9d6a4a] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm">
        <Sparkles className="h-3 w-3" />Versione Pro
        {proPaid ? null : countdown ? (
          <span data-testid="cliente-pro-trial" className="font-bold normal-case tracking-normal opacity-90">· Concessione gratuita · {countdown}</span>
        ) : (
          <span className="font-bold normal-case tracking-normal opacity-90">· Concessione gratuita</span>
        )}
      </span>
    );
  }

  const abbonati = async () => {
    setBusy(true);
    try {
      const r = await src.proCheckout(interval);
      if (!r?.checkout_url) throw new Error("URL pagamento mancante");
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile avviare il pagamento");
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="passa-pro-btn"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#C17F59]/40 bg-[#FBF3EE] px-3 py-1 text-[11px] font-bold text-[#9d6a4a] transition-colors hover:bg-[#f6e7dd]">
        Passa a Versione Pro<Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 sm:items-center sm:p-4" onClick={() => setOpen(false)} data-testid="passa-pro-dialog">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-r from-[#C17F59] to-[#9d6a4a] text-white"><Sparkles className="h-5 w-5" /></span>
                <h3 className="text-lg font-extrabold text-slate-900">Versione Pro</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-100" aria-label="Chiudi" data-testid="passa-pro-close"><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-4 text-sm leading-relaxed text-slate-600">La <b>Versione Pro</b> sblocca le funzioni avanzate pensate per le aziende: <b>domande illimitate</b> a Vesta e <b>report mensili</b>, <b>dettaglio tecnico degli allarmi</b> con <b>richiesta d'intervento</b> diretta a Torri, per l'auto la <b>ricarica a PUN</b> con scelta del modello e stima del risparmio, <b>esportazione dati (CSV)</b>, <b>salva schermo personalizzato</b> con logo e video aziendale, il <b>logo della tua azienda</b> nel portale e <b>più accessi</b> per i referenti.</p>

            {canBuy && (
              <div className="mt-4 rounded-2xl border border-[#C17F59]/30 bg-[#FBF3EE] p-4" data-testid="passa-pro-abbonamento">
                {!tar ? <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#C17F59]" /></div>
                  : !tier ? (
                    <p className="text-center text-sm font-semibold text-[#9d6a4a]" data-testid="passa-pro-oltre">Impianto oltre 1 MW: contatta Torri per una tariffa dedicata.</p>
                  ) : (
                    <>
                      <div className="mb-3 flex items-center justify-between">
                        <div>
                          <div className="text-[11px] font-bold uppercase tracking-wider text-[#9d6a4a]">Tariffa {tier.label}</div>
                          <div className="text-[11px] text-slate-500">{tier.range}</div>
                        </div>
                        <div className="inline-flex rounded-full bg-white p-0.5 ring-1 ring-[#C17F59]/30" data-testid="passa-pro-ciclo">
                          <button type="button" onClick={() => setInterval("month")} data-testid="passa-pro-mensile"
                            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${interval === "month" ? "bg-[#C17F59] text-white" : "text-[#9d6a4a]"}`}>Mensile</button>
                          <button type="button" onClick={() => setInterval("year")} data-testid="passa-pro-annuale"
                            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${interval === "year" ? "bg-[#C17F59] text-white" : "text-[#9d6a4a]"}`}>Annuale −{tar.sconto_annuale_pct}%</button>
                        </div>
                      </div>
                      <div className="flex items-end gap-1" data-testid="passa-pro-prezzo">
                        <span className="text-3xl font-extrabold text-slate-900">{eur(interval === "year" ? tier.yearly_per_month : tier.monthly)} €</span>
                        <span className="pb-1 text-sm font-semibold text-slate-500">/mese</span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {interval === "year" ? `Fatturato ${eur(tier.yearly)} € all'anno · IVA inclusa` : "Addebito mensile · IVA inclusa · disdici quando vuoi"}
                      </p>
                      <button type="button" onClick={abbonati} disabled={busy} data-testid="passa-pro-abbonati"
                        className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#C17F59] to-[#9d6a4a] font-bold text-white transition-opacity hover:opacity-95 disabled:opacity-50">
                        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-5 w-5" />Abbonati ora</>}
                      </button>
                      <p className="mt-2 text-center text-[11px] text-slate-400">Pagamento sicuro con Stripe</p>
                    </>
                  )}
              </div>
            )}
            <img src="/confronto-versione-pro.png" alt="Confronto tra versione Normale e Versione Pro" className="mt-4 w-full rounded-2xl ring-1 ring-slate-100" data-testid="passa-pro-img" />
            {!canBuy && <p className="mt-4 rounded-2xl bg-[#FBF3EE] p-3 text-center text-sm font-semibold text-[#9d6a4a]">Chiedi al tuo installatore Torri per attivare la Versione Pro.</p>}
          </div>
        </div>
      )}
    </>
  );
}
